import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import io from 'socket.io-client';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface PendingMatch {
  id: string;
  mode: string;
  startTime: string;
  participants: Array<{
    id: string;
    side: string;
    team?: {
      name: string;
    };
  }>;
}

const PendingMatchNotifications = () => {
  const [pendingMatches, setPendingMatches] = useState<PendingMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchPendingMatches = useCallback(async () => {
    try {
      const response = await api.get('/matches/pending');
      setPendingMatches(response.data);
    } catch (error) {
      console.error('Failed to fetch pending matches:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch if user is logged in
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    fetchPendingMatches();

    // Poll for new pending matches every 30 seconds as a fallback
    const interval = setInterval(fetchPendingMatches, 30000);

    return () => clearInterval(interval);
  }, [fetchPendingMatches]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, { transports: ['websocket'] });
    socket.on('pendingMatchCreated', fetchPendingMatches);
    socket.on('pendingMatchAccepted', fetchPendingMatches);
    socket.on('pendingMatchRejected', fetchPendingMatches);

    return () => {
      socket.off('pendingMatchCreated', fetchPendingMatches);
      socket.off('pendingMatchAccepted', fetchPendingMatches);
      socket.off('pendingMatchRejected', fetchPendingMatches);
      socket.disconnect();
    };
  }, [fetchPendingMatches]);

  if (loading || pendingMatches.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-r from-yellow-400 to-orange-400 border-b-2 border-yellow-600 shadow-md">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="text-2xl">⚡</div>
            <div>
              <div className="font-bold text-gray-900">
                {pendingMatches.length === 1
                  ? 'Máte 1 novou výzvu k zápasu!'
                  : `Máte ${pendingMatches.length} nové výzvy k zápasům!`}
              </div>
              <div className="text-sm text-gray-800">
                {pendingMatches.map((match, index) => {
                  const hostTeam = match.participants.find(p => p.side === 'left')?.team?.name || 'Tým';
                  return (
                    <span key={match.id}>
                      {hostTeam}
                      {index < pendingMatches.length - 1 ? ', ' : ''}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate(`/matches/${pendingMatches[0].id}`)}
            className="px-6 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-lg shadow-card hover:shadow-glow transition-all duration-200"
          >
            Zobrazit výzvy
          </button>
        </div>
      </div>
    </div>
  );
};

export default PendingMatchNotifications;
